import {
  PerfilSeccionEstado,
  PerfilSeccionId,
} from '../../data/perfil-microempresario.content';

/** Campo de formulario (datos fijos de la sección, no documentos). */
export interface PerfilProgressFieldDef {
  key: string;
  /** Default true. Opcionales no suman al denominador. */
  required?: boolean;
  /**
   * Solo cuenta como obligatorio si otro campo del form tiene este valor.
   * Ej. fecha Cámara solo si tieneCamaraComercio === 'si'.
   */
  requiredWhen?: { key: string; equals: unknown };
}

/**
 * Documento parametrizable por admin/back.
 * Obligatoriedad y sección vienen de BD.
 */
export interface PerfilDocumentoDef {
  /** Id del tipo / parametrización. */
  id: string;
  codigo: string;
  label: string;
  seccionId: PerfilSeccionId;
  obligatorio: boolean;
  /** Clave en form / values: `doc_{idTipoDocumento}`. */
  valueKey: string;
  pasoId?: string;
  /** Grupo: texto e imagen del modal (NEO_GRUPO_TIPO_DOCUMENTO). */
  grupoId?: number;
  descripcion?: string;
  guiaUsuario?: string;
  /** FK opcional → NEO_ARCHIVO_PUBLICO; URL se resuelve al abrir modal. */
  idArchivoPublico?: number | null;
  extensionesValidas?: string;
  /** Metadatos de upload (tiposDocumentos). */
  idTipoDocumento?: number;
  tipoArchivoPermitido?: string;
  nombreArchivoGuardado?: string;
  esFoto?: boolean;
  orden?: number;
  multiple?: boolean;
  cantidadArchivos?: number;
  idDocumentoSolicitud?: string | number | null;
}

export interface UploadedTipoDocumento {
  idTipoDocumento?: number;
  nombre?: string;
  obligatorio?: boolean | number;
  estado?: string;
  seccionUbicacion?: string | null;
  extensionesValidas?: string;
  tipoArchivoPermitido?: string;
  nombreArchivoGuardado?: string;
  foto?: number | boolean;
  orden?: number;
  neoDocumentosSolicitudDTO?: Array<{
    idDocumentoSolicitud?: number;
    nombreDocumento?: string;
    habilitado?: number;
    estadoDocumentoOCR?: number | null;
    observacionDocumentoOCR?: string | null;
    pesoArchivo?: number | null;
  }>;
}

export interface PerfilProgressSubsectionDef {
  id: string;
  label: string;
  fields: ReadonlyArray<PerfilProgressFieldDef>;
}

export interface PerfilProgressSectionDef {
  id: PerfilSeccionId;
  /** Solo campos de formulario estables (sin documentos). */
  subsections: ReadonlyArray<PerfilProgressSubsectionDef>;
  /** Si no se define, = subsections resueltas (con documentos). */
  checkpoints?: number;
}

export interface PerfilSubsectionProgress {
  id: string;
  label: string;
  filledRequired: number;
  totalRequired: number;
  percent: number;
}

export interface PerfilSectionProgress {
  id: PerfilSeccionId;
  filledRequired: number;
  totalRequired: number;
  percent: number;
  checkpoints: number;
  estado: PerfilSeccionEstado;
  subsections: PerfilSubsectionProgress[];
  /** Documentos vigentes en esta sección (config dinámica). */
  documentos: ReadonlyArray<PerfilDocumentoDef>;
}

export interface PerfilProgressSnapshot {
  sections: PerfilSectionProgress[];
  globalPercent: number;
  seccionesCompletas: number;
}

export type PerfilProgressValues = Record<string, unknown>;

/**
 * Campos de formulario por sección (estables).
 */
export const PERFIL_PROGRESS_BASE_DEFINITIONS: ReadonlyArray<PerfilProgressSectionDef> =
  [
    {
      id: 'sobre-ti',
      subsections: [
        {
          id: 'datos-personales',
          label: 'Datos personales',
          fields: [
            { key: 'genero' },
            { key: 'fechaNacimiento' },
            { key: 'fechaExpedicion' },
            { key: 'estadoCivil' },
            { key: 'nivelEstudios' },
            { key: 'personasACargo' },
            { key: 'seguridadSocial' },
            { key: 'sisben' },
            {
              key: 'idSisben',
              requiredWhen: { key: 'sisben', equals: 'si' },
            },
            { key: 'creditoFormal' },
            {
              key: 'fechaDesembolso',
              requiredWhen: { key: 'creditoFormal', equals: 'si' },
            },
          ],
        },
        // Subsección "documentos" se arma en runtime con la config de docs.
      ],
    },
    {
      id: 'sobre-negocio',
      /** 3 nodos → ubicación + fotos + info adicional. Docs se inyectan por pasoId. */
      checkpoints: 3,
      subsections: [
        {
          id: 'ubicacion',
          label: 'Ubicación y dirección',
          fields: [
            { key: 'nombreNegocio' },
            { key: 'departamento' },
            { key: 'ciudad' },
            // Solo Bogotá (11001); se activa en isFieldRequired vía ciudad.
            {
              key: 'localidad',
              requiredWhen: { key: 'ciudad', equals: 11001 },
            },
            { key: 'barrio' },
            { key: 'direccion' },
            { key: 'tipoVivienda' },
            { key: 'tieneRut' },
            { key: 'tieneCamaraComercio' },
            {
              key: 'fechaRegistroCamara',
              requiredWhen: { key: 'tieneCamaraComercio', equals: 'si' },
            },
          ],
        },
        {
          id: 'fotos',
          label: 'Fotos del negocio',
          // Slots vienen solo del catálogo (flag foto / pasoId).
          fields: [],
        },
        {
          id: 'info-adicional',
          label: 'Información adicional',
          // Redes: catálogo dinámico (no suman al %). Docs opcionales por pasoId.
          fields: [],
        },
      ],
    },
    {
      id: 'sobre-ingresos',
      /** 3 nodos: activos → flujo de caja → info adicional. Docs por pasoId. */
      checkpoints: 3,
      subsections: [
        {
          id: 'activos-soportes',
          label: 'Activos y soportes',
          fields: [
            { key: 'anioActivos' },
            { key: 'valorActivos' },
            { key: 'declaraRenta' },
            { key: 'autorizacionCentrales' },
          ],
        },
        {
          id: 'flujo-caja',
          label: 'Flujo de caja',
          fields: [
            { key: 'mesReporte' },
            { key: 'ventasUltimoMes' },
            { key: 'otrosIngresos', required: false },
            { key: 'fuenteOtrosIngresos', required: false },
            { key: 'pagoProveedores', required: false },
            { key: 'arriendo', required: false },
            { key: 'administracion', required: false },
            { key: 'serviciosPublicosNegocio', required: false },
            { key: 'sueldoPropio', required: false },
            { key: 'sueldoEmpleados', required: false },
            { key: 'planCelular', required: false },
            { key: 'tarjetaCredito', required: false },
            { key: 'tieneCreditos', required: false },
            { key: 'cuotaCreditos', required: false },
            { key: 'saldoDeudas', required: false },
            { key: 'otrosGastos', required: false },
            { key: 'fuenteOtrosGastos', required: false },
            { key: 'modoGastos', required: false },
            {
              key: 'gastosGeneralesMonto',
              requiredWhen: { key: 'modoGastos', equals: 'generales' },
            },
          ],
        },
        {
          id: 'info-adicional',
          label: 'Información adicional',
          fields: [{ key: 'observaciones', required: false }],
        },
      ],
    },
  ];

/**
 * PJ (HU 8189): 2 secciones × 50 %.
 * `sobre-ti` = hub «Sobre la empresa y tu» (datos + docs/fotos + opcionales).
 * Docs admin `SOBRE_NEGOCIO` se remapean a `sobre-ti` en catálogo PJ.
 */
export const PERFIL_PROGRESS_BASE_DEFINITIONS_PJ: ReadonlyArray<PerfilProgressSectionDef> =
  [
    {
      id: 'sobre-ti',
      checkpoints: 3,
      subsections: [
        {
          id: 'datos-empresa',
          label: 'Datos de la empresa',
          fields: [
            { key: 'genero' },
            { key: 'minoria' },
            {
              key: 'minoriaDescripcion',
              requiredWhen: { key: 'minoria', equals: 'si' },
            },
            { key: 'creditoFormal' },
            {
              key: 'fechaDesembolso',
              requiredWhen: { key: 'creditoFormal', equals: 'si' },
            },
            { key: 'razonSocial' },
            { key: 'actividadEconomica' },
            { key: 'tieneCamaraComercio' },
            {
              key: 'fechaRegistroCamara',
              requiredWhen: { key: 'tieneCamaraComercio', equals: 'si' },
            },
            { key: 'departamento' },
            { key: 'ciudad' },
            {
              key: 'localidad',
              requiredWhen: { key: 'ciudad', equals: 11001 },
            },
            { key: 'barrio' },
            { key: 'direccion' },
            { key: 'tipoSociedad' },
          ],
        },
        {
          id: 'fotos',
          label: 'Fotos del negocio',
          fields: [],
        },
        {
          id: 'info-adicional',
          label: 'Información adicional',
          fields: [],
        },
      ],
    },
    {
      id: 'sobre-ingresos',
      /** 3 nodos Figma: datos → documentos → opcionales. */
      checkpoints: 3,
      subsections: [
        {
          id: 'activos-soportes',
          label: 'Activos y soportes',
          fields: [
            { key: 'anioActivos' },
            { key: 'ventasUltimoAnio' },
            { key: 'valorActivos' },
            { key: 'autorizacionCentrales' },
          ],
        },
        {
          id: 'info-adicional',
          label: 'Información adicional',
          fields: [{ key: 'observaciones', required: false }],
        },
      ],
    },
  ];

/**
 * Id de la subsección dinámica de documentos (paso por defecto).
 */
export const PERFIL_PROGRESS_DOCS_SUBSECTION_ID = 'documentos';

const PASO_LABELS: Record<string, string> = {
  documentos: 'Documentos',
  fotos: 'Fotos del negocio',
  'info-adicional': 'Información adicional',
  'activos-soportes': 'Activos y soportes',
};

export function labelForPasoId(pasoId: string): string {
  return PASO_LABELS[pasoId] ?? 'Documentos';
}
